import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { FormProvider } from 'react-hook-form'
import { skipToken } from '@reduxjs/toolkit/query'

import BasicTabs from 'components/tabs/basic-tabs/basic-tabs.component'
import TabPanel from 'components/tabs/tab-panel/tab-panel.component'
import LoadCustomerForm from 'components/forms/load-customer-form/load-customer-form.component'
import { CompanySuggestion } from 'components/forms/load-customer-form/load-customer-form.component'
import LoadRateForm from 'components/forms/load-rate-form/load-rate-form.component'
import LoadPickupForm from 'components/forms/load-pickup-form/load-pickup-form.component'
import LoadDeliveryForm from 'components/forms/load-delivery-form/load-delivery-form.component'
import LoadNotesForm from 'components/forms/load-notes-form/load-notes-form.component'
import ConfirmationDialog from 'components/confirmation-dialog/confirmation-dialog.component'
import { EditModeProvider } from 'contexts/edit-mode.context'
import useSubmitLock from 'hooks/use-submit-lock'

import { verifyOrderTabs } from 'pages/verify-order/verify-order.constant'
import {
  useVerifyOrderForm,
  VerifyOrderFormValues,
} from 'pages/verify-order/verify-order-form/verify-order-form.hooks'
import { verifyOrderFormSchema } from 'pages/verify-order/verify-order-form/verify-order-form.schema'
import { Order, OrderEvent } from 'store/orders/orders.types'
import { CreateOrderBody, CreateOrderEventBody } from 'store/orders/orders.types'
import {
  useCreateOrderMutation,
  useGenerateDefaultLoadConfirmationPdfMutation,
} from 'store/orders/orders.api'
import {
  useCreateCompanyMutation,
  useCreateUserCompanyAssociationMutation,
  useGetUserCompanyAssociationByUserQuery,
  useLazyGetCompanyByIdQuery,
} from 'store/company/company.api'
import { Company, CompanyCreateResponse } from 'store/company/company.types'
import { useUploadLoadConfirmationToOrderMutation, useEnsureInvoiceForOrderMutation } from 'store/task-automation/task-automation.api'
import { isOrderDetailsIncomplete } from 'components/update-order-confirmation-dialog/update-order-confirmation-dialog.hooks'
import { formatPhoneForDisplay, formatPhoneForSave } from 'utils/phone'
import { getUserId } from 'utils/common'
import { parseFreeformAddress } from 'utils/parse-freeform-address'

const emptyOrderEvent = (eventType: 'PICKUP' | 'DELIVERY'): OrderEvent => ({
  name: '',
  address: '',
  startTime: '',
  endTime: '',
  eventType,
  loadType: 'FTL',
  loadCount: 0,
  temperatureValue: 0,
  temperatureUnit: 'C',
  hazmat: '',
  weightValue: 0,
  weightUnit: '',
  pickupNumber: '',
  notes: '',
  status: 'NEW',
})

const manualTemplateOrder: Order = {
  orderId: 0,
  userOrderId: '',
  status: 'NEW',
  loadNumber: '',
  companyId: '',
  customerId: '',
  customerName: '',
  notificationEmail: '',
  billingEmail: '',
  baseRate: 0,
  detentionCharges: 0,
  layoverCharges: 0,
  fuelSurcharge: 0,
  otherCharges: 0,
  notes: '',
  accountPayableEmail: '',
  currency: 'CAD',
  orderEvents: [emptyOrderEvent('PICKUP'), emptyOrderEvent('DELIVERY')],
}

export type CreateManualOrderDialogProps = {
  open: boolean
  onClose: () => void
  onCreated?: () => void
  templateOrder?: Order | null
  templateCompany?: Company | null
}

function buildRepeatOrderFormValues(
  order: Order,
  company?: Company | null
): VerifyOrderFormValues {
  const address = company
    ? `${company.streetNumber || ''} ${company.streetName || ''} ${company.city || ''} ${company.state || ''}`.trim()
    : ''

  const orderEvents = order.orderEvents?.length
    ? order.orderEvents
    : [emptyOrderEvent('PICKUP'), emptyOrderEvent('DELIVERY')]

  const formattedOrderEvents = orderEvents.map((event) => ({
    startTime: '',
    endTime: '',
    name: event.name || '',
    address: event.address || '',
    loadType: event.loadType || '',
    loadCount: event.loadCount?.toString() || '',
    temperatureValue: event.temperatureValue?.toString() || '',
    hazmat: event.hazmat || '',
    pickupNumber: event.pickupNumber || '',
    weightValue: event.weightValue?.toString() || '',
    notes: event.notes || '',
    _orderEventId: '',
    _eventType: event.eventType || '',
  }))

  return verifyOrderFormSchema.cast({
    customerName: company?.companyName ?? order.customerName ?? '',
    address,
    loadNumber: '',
    billingEmail: company?.invoicePodEmail || order.billingEmail || '',
    notificationEmail: company?.notificationEmail || order.notificationEmail || '',
    accountPayableEmail: company?.accountPayableEmail || order.accountPayableEmail || '',
    phone: formatPhoneForDisplay(company?.phoneNumber || ''),
    other: '',
    currency: order.currency || 'CAD',
    baseRate: order.baseRate?.toString() || '',
    detentionCharges: order.detentionCharges?.toString() || '',
    layoverCharges: order.layoverCharges?.toString() || '',
    fuelSurcharge: order.fuelSurcharge?.toString() || '',
    otherCharges: order.otherCharges?.toString() || '',
    notes: order.notes || '',
    orderEvents: formattedOrderEvents,
  })
}

const defaultEventFormRow = (eventType: 'PICKUP' | 'DELIVERY') => ({
  startTime: '',
  endTime: '',
  name: '',
  address: '',
  loadType: '',
  loadCount: '',
  temperatureValue: '',
  hazmat: '',
  pickupNumber: '',
  weightValue: '',
  notes: '',
  _orderEventId: '',
  _eventType: eventType,
})

const manualOrderFormDefaults: VerifyOrderFormValues = verifyOrderFormSchema.cast({
  customerName: '',
  address: '',
  loadNumber: '',
  billingEmail: '',
  notificationEmail: '',
  accountPayableEmail: '',
  phone: '',
  other: '',
  currency: '',
  baseRate: '',
  detentionCharges: '',
  layoverCharges: '',
  fuelSurcharge: '',
  otherCharges: '',
  notes: '',
  orderEvents: [defaultEventFormRow('PICKUP'), defaultEventFormRow('DELIVERY')],
})

function formValuesToOrderForValidation(
  orderData: Record<string, unknown>,
  orderEvents: Array<Record<string, unknown>>
): Order {
  const events: OrderEvent[] = orderEvents.map((ev, idx) => ({
    name: (ev.name as string) || '',
    address: (ev.address as string) || '',
    startTime: (ev.startTime as string) || '',
    endTime: (ev.endTime as string) || '',
    eventType: (idx === 0 ? 'PICKUP' : 'DELIVERY') as OrderEvent['eventType'],
    loadType: ((ev.loadType as string) || 'FTL') as OrderEvent['loadType'],
    loadCount: parseInt(String(ev.loadCount || '0'), 10) || 0,
    temperatureValue: parseFloat(String(ev.temperatureValue || '0')) || 0,
    temperatureUnit: 'C',
    hazmat: (ev.hazmat as string) || '',
    weightValue: parseFloat(String(ev.weightValue || '0')) || 0,
    weightUnit: (ev.weightUnit as string) || '',
    pickupNumber: (ev.pickupNumber as string) || '',
    notes: (ev.notes as string) || '',
    status: 'NEW',
  }))

  return {
    ...manualTemplateOrder,
    customerName: (orderData.customerName as string) || '',
    loadNumber: (orderData.loadNumber as string) || '',
    billingEmail: (orderData.billingEmail as string) || '',
    baseRate: parseFloat(String(orderData.baseRate || '')) || 0,
    orderEvents: events,
  }
}

function mapFormEventToCreateBody(
  formEvent: Record<string, unknown>,
  eventType: 'PICKUP' | 'DELIVERY'
): CreateOrderEventBody {
  const loadTypeRaw = (formEvent.loadType as string) || 'FTL'
  return {
    name: (formEvent.name as string) || '',
    address: (formEvent.address as string) || '',
    startTime: (formEvent.startTime as string) || null,
    endTime: (formEvent.endTime as string) || null,
    eventType,
    loadType: loadTypeRaw === 'RTL' ? 'RTL' : 'FTL',
    loadCount: parseInt(String(formEvent.loadCount || '0'), 10) || 0,
    temperatureValue: parseFloat(String(formEvent.temperatureValue || '0')) || 0,
    temperatureUnit: 'C',
    hazmat: (formEvent.hazmat as string) || '',
    weightValue: parseFloat(String(formEvent.weightValue || '0')) || 0,
    weightUnit: (formEvent.weightUnit as string) || 'lb',
    pickupNumber: (formEvent.pickupNumber as string) || '',
    notes: (formEvent.notes as string) || '',
  }
}

const CreateManualOrderDialog = ({
  open,
  onClose,
  onCreated,
  templateOrder = null,
  templateCompany = null,
}: CreateManualOrderDialogProps) => {
  const [tabsValue, setTabsValue] = useState(0)
  const [orderEventsWithIds, setOrderEventsWithIds] = useState<
    Array<OrderEvent & { _formIndex?: number }>
  >([])
  const [attachFile, setAttachFile] = useState<File | null>(null)
  const [confirmIncompleteOpen, setConfirmIncompleteOpen] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [isCreatingOrder, setIsCreatingOrder] = useState(false)
  const { isSubmitting, wrapFormSubmit } = useSubmitLock()
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const formMethods = useVerifyOrderForm(manualTemplateOrder, manualOrderFormDefaults)

  const [createCompany] = useCreateCompanyMutation()
  const [createAssociation] = useCreateUserCompanyAssociationMutation()
  const [fetchCompanyById] = useLazyGetCompanyByIdQuery()
  const [createOrder] = useCreateOrderMutation()
  const [generateDefaultLoadConfirmationPdf, { isLoading: isGeneratingDefaultLc }] =
    useGenerateDefaultLoadConfirmationPdfMutation()
  const [uploadLcToOrder, { isLoading: isUploadingLc }] =
    useUploadLoadConfirmationToOrderMutation()
  const [ensureInvoiceForOrder, { isLoading: isEnsuringInvoice }] =
    useEnsureInvoiceForOrderMutation()

  const isBusy =
    isCreatingOrder ||
    isSubmitting ||
    formMethods.formState.isSubmitting ||
    isUploadingLc ||
    isEnsuringInvoice ||
    isGeneratingDefaultLc

  const userId = getUserId()
  const { data: userCompanyAssociations = [], isFetching: isFetchingUserCompanies } =
    useGetUserCompanyAssociationByUserQuery(
      open && userId ? { userId } : skipToken
    )
  const [userCompanies, setUserCompanies] = useState<Company[]>([])

  useEffect(() => {
    if (!open) return

    let mounted = true
    const loadCompanies = async () => {
      if (!userCompanyAssociations.length) {
        if (mounted) setUserCompanies([])
        return
      }
      const companyIds = Array.from(new Set(userCompanyAssociations.map((assoc) => assoc.companyId)))
      const companyResults = await Promise.all(
        companyIds.map(async (companyId) => {
          try {
            return await fetchCompanyById({ id: companyId }).unwrap()
          } catch (error) {
            console.error('Failed to fetch company by id', companyId, error)
            return null
          }
        })
      )
      if (mounted) {
        setUserCompanies(companyResults.filter((company): company is Company => !!company))
      }
    }
    void loadCompanies()
    return () => {
      mounted = false
    }
  }, [fetchCompanyById, open, userCompanyAssociations])

  const companySuggestions = useMemo<CompanySuggestion[]>(
    () =>
      userCompanies.map((company) => {
        const addressParts = [
          [company.streetNumber, company.streetName].filter(Boolean).join(' ').trim(),
          company.city,
          company.state,
          company.zipCode,
        ].filter(Boolean)
        return {
          id: company.id,
          companyName: company.companyName || '',
          address: addressParts.join(', '),
          billingEmail: company.invoicePodEmail || '',
          notificationEmail: company.notificationEmail || '',
          accountPayableEmail: company.accountPayableEmail || '',
          phone: formatPhoneForDisplay(company.phoneNumber || ''),
        }
      }),
    [userCompanies]
  )

  const { pickupIndices, deliveryIndices, pickupCount, deliveryCount } = useMemo(() => {
    const orderEvents = formMethods.watch('orderEvents') || []
    const pickups: number[] = []
    const deliveries: number[] = []
    orderEvents.forEach((_: unknown, index: number) => {
      const storedEvent = orderEventsWithIds[index]
      const eventType = storedEvent?.eventType || 'PICKUP'
      if (eventType === 'PICKUP') pickups.push(index)
      else if (eventType === 'DELIVERY') deliveries.push(index)
    })
    return {
      pickupIndices: pickups,
      deliveryIndices: deliveries,
      pickupCount: pickups.length,
      deliveryCount: deliveries.length,
    }
  }, [formMethods, orderEventsWithIds])

  const hasPickup = pickupCount > 0
  const hasDelivery = deliveryCount > 0

  const dynamicTabs = useMemo(() => {
    const tabs = [...verifyOrderTabs]
    if (pickupCount > 0) {
      const pickupTabIndex = tabs.findIndex((tab) => tab.id === 'pickup')
      if (pickupTabIndex !== -1) {
        tabs.splice(pickupTabIndex, 1)
        pickupIndices.forEach((_, idx) => {
          const label = pickupCount === 1 ? 'Pickup' : `Pickup ${idx + 1}`
          tabs.splice(pickupTabIndex + idx, 0, { id: `pickup${idx + 1}`, label })
        })
      }
    }
    if (deliveryCount > 0) {
      const deliveryTabIndex = tabs.findIndex((tab) => tab.id === 'delivery')
      if (deliveryTabIndex !== -1) {
        tabs.splice(deliveryTabIndex, 1)
        deliveryIndices.forEach((_, idx) => {
          const label = deliveryCount === 1 ? 'Delivery' : `Delivery ${idx + 1}`
          tabs.splice(deliveryTabIndex + idx, 0, { id: `delivery${idx + 1}`, label })
        })
      }
    }
    return tabs
  }, [pickupCount, deliveryCount, pickupIndices, deliveryIndices])

  useEffect(() => {
    if (!open) return

    setAttachFile(null)
    setTabsValue(0)
    setSubmitError('')
    setConfirmIncompleteOpen(false)

    if (templateOrder) {
      const sourceEvents = templateOrder.orderEvents?.length
        ? templateOrder.orderEvents
        : [emptyOrderEvent('PICKUP'), emptyOrderEvent('DELIVERY')]

      const eventsWithIds = sourceEvents.map((event, index) => ({
        ...event,
        startTime: '',
        endTime: '',
        orderEventId: undefined,
        _formIndex: index,
      }))
      setOrderEventsWithIds(eventsWithIds)
      setSelectedCompany(templateCompany ?? null)
      formMethods.reset(buildRepeatOrderFormValues(templateOrder, templateCompany))
    } else {
      const eventsWithIds = (manualTemplateOrder.orderEvents || []).map((event, index) => ({
        ...event,
        _formIndex: index,
      }))
      setOrderEventsWithIds(eventsWithIds)
      setSelectedCompany(null)
      formMethods.reset(manualOrderFormDefaults)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateOrder, templateCompany, formMethods.reset])

  const handleCompanySelected = (companySuggestion: CompanySuggestion | null) => {
    if (!companySuggestion) {
      setSelectedCompany(null)
      return
    }
    const company =
      userCompanies.find((item) => item.id === companySuggestion.id) ?? null
    setSelectedCompany(company)
  }

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabsValue(newValue)
  }

  const handleAddEvent = (eventType: 'PICKUP' | 'DELIVERY') => {
    const currentOrderEvents = formMethods.getValues('orderEvents') || []
    const newEvent = {
      ...defaultEventFormRow(eventType),
    }
    formMethods.setValue('orderEvents', [...currentOrderEvents, newEvent])
    const newEventWithId: OrderEvent & { _formIndex?: number } = {
      ...emptyOrderEvent(eventType),
      _formIndex: currentOrderEvents.length,
    }
    const updatedEventsWithIds = [...orderEventsWithIds, newEventWithId]
    setOrderEventsWithIds(updatedEventsWithIds)
    const pickupCnt = updatedEventsWithIds.filter((e) => e.eventType === 'PICKUP').length
    const deliveryCnt = updatedEventsWithIds.filter((e) => e.eventType === 'DELIVERY').length
    if (eventType === 'PICKUP') {
      setTabsValue(2 + pickupCnt - 1)
    } else {
      setTabsValue(2 + pickupCnt + deliveryCnt - 1)
    }
  }

  const handleDeleteEvent = (eventIndex: number) => {
    const currentOrderEvents = formMethods.getValues('orderEvents') || []
    const eventToDelete = currentOrderEvents[eventIndex] as { _orderEventId?: string }
    if (eventToDelete?._orderEventId) return
    const updatedEvents = currentOrderEvents.filter(
      (_: unknown, index: number) => index !== eventIndex
    )
    formMethods.setValue('orderEvents', updatedEvents)
    const updatedEventsWithIds = orderEventsWithIds.filter((_, index) => index !== eventIndex)
    setOrderEventsWithIds(updatedEventsWithIds)
    if (tabsValue > eventIndex) {
      setTabsValue(Math.max(0, tabsValue - 1))
    }
  }

  const performCreate = async () => {
    setIsCreatingOrder(true)
    setSubmitError('')
    try {
      const userId = getUserId()
      if (!userId) {
        setSubmitError('You must be signed in to create an order.')
        return
      }

      const orderData = formMethods.getValues() ?? manualOrderFormDefaults
      const rawEvents = (orderData.orderEvents || []) as Array<Record<string, unknown>>

      const addrParts = parseFreeformAddress((orderData.address as string) || '')

      const selectedExistingCompany =
        selectedCompany ||
        userCompanies.find(
          (company) =>
            company.companyName.trim().toLowerCase() ===
            ((orderData.customerName as string) || '').trim().toLowerCase()
        ) ||
        null

      let company: CompanyCreateResponse
      if (selectedExistingCompany) {
        company = {
          ...selectedExistingCompany,
          notificationEmailId: '',
          invoicePodEmailId: '',
          accountPayableEmailId: '',
        }
      } else {
        try {
          company = await createCompany({
          userId,
          companyName: (orderData.customerName as string) || '',
          ...addrParts,
          notificationEmail: (orderData.notificationEmail as string) || '',
          invoicePodEmail: (orderData.billingEmail as string) || '',
          accountPayableEmail: (orderData.accountPayableEmail as string) || '',
          phoneNumber: formatPhoneForSave((orderData.phone as string) || ''),
          }).unwrap()
        } catch (e) {
          console.error(e)
          setSubmitError('Could not create or resolve company. Check details and try again.')
          return
        }

        try {
          await createAssociation({
          userId,
          companyId: company.id,
          association: 'Customer',
          factoringEnabled: false,
          factoringTerm: 0,
          currency: 'CAD',
          }).unwrap()
        } catch (e) {
          console.error(e)
          setSubmitError('Company was created but linking to your account failed.')
          return
        }
      }

      const companyId = company.id
      const notificationRef = company.notificationEmailId ?? company.notificationEmail ?? ''
      const billingRef = company.invoicePodEmailId ?? company.invoicePodEmail ?? ''
      const apRef =
        company.accountPayableEmailId ?? company.accountPayableEmail ?? company.invoicePodEmailId ?? company.invoicePodEmail ?? ''

      const orderEventsBody: CreateOrderEventBody[] = rawEvents.map((ev, index) => {
        const stored = orderEventsWithIds[index]
        const eventType = (stored?.eventType ||
          (ev._eventType as string) ||
          (index === 0 ? 'PICKUP' : 'DELIVERY')) as 'PICKUP' | 'DELIVERY'
        return mapFormEventToCreateBody(ev, eventType)
      })

      const body: CreateOrderBody = {
        status: 'NEW',
        loadNumber: (orderData.loadNumber as string) || '',
        companyId,
        customerId: companyId,
        customerName: (orderData.customerName as string) || '',
        notificationEmail: notificationRef,
        billingEmail: billingRef,
        accountPayableEmail: apRef,
        baseRate: parseFloat(String(orderData.baseRate || '0')) || 0,
        detentionCharges: parseFloat(String(orderData.detentionCharges || '0')) || 0,
        layoverCharges: parseFloat(String(orderData.layoverCharges || '0')) || 0,
        fuelSurcharge: parseFloat(String(orderData.fuelSurcharge || '0')) || 0,
        otherCharges: parseFloat(String(orderData.otherCharges || '0')) || 0,
        notes: ((orderData.notes as string) || '').trim() || ' ',
        currency: (orderData.currency as 'CAD' | 'USD') || 'CAD',
        orderEvents: orderEventsBody,
      }

      let created: Order
      try {
        created = await createOrder(body).unwrap()
      } catch (e) {
        console.error(e)
        setSubmitError('Order could not be created. Please verify dates and required fields.')
        return
      }

      const orderId = created.orderId
      if (!orderId) {
        setSubmitError('Order was created but the response was incomplete. Please try again.')
        return
      }

      try {
        if (attachFile) {
          await uploadLcToOrder({ orderId, file: attachFile }).unwrap()
        } else {
          await generateDefaultLoadConfirmationPdf({ orderId }).unwrap()
        }
        await ensureInvoiceForOrder({ orderId }).unwrap()
      } catch (e) {
        console.error(e)
        setSubmitError(
          attachFile
            ? 'Order was created but uploading the load confirmation or invoice setup failed. You can add documents or finish setup from order details.'
            : 'Order was created but generating the load confirmation or invoice setup failed. You can add a document or finish setup from order details.'
        )
        onCreated?.()
        onClose()
        return
      }

      onCreated?.()
      onClose()
    } finally {
      setIsCreatingOrder(false)
    }
  }

  const onValidSubmit = async (orderData: VerifyOrderFormValues) => {
    const rawEvents = (orderData.orderEvents || []) as Array<Record<string, unknown>>
    const preview = formValuesToOrderForValidation(orderData, rawEvents)
    if (isOrderDetailsIncomplete(preview)) {
      setConfirmIncompleteOpen(true)
      return
    }
    await performCreate()
  }

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ pr: 5 }}>
        {templateOrder ? 'Repeat order' : 'Create order manually'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isBusy}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <EditModeProvider initialEditMode suppressAutofill>
        <FormProvider {...formMethods}>
          <form
            autoComplete="off"
            onSubmit={wrapFormSubmit(formMethods.handleSubmit, onValidSubmit)}
          >
            {/* Decoy fields: Chrome fills these instead of real inputs */}
            <input
              type="text"
              name="prevent_autofill_username"
              autoComplete="username"
              tabIndex={-1}
              aria-hidden
              style={{
                position: 'fixed',
                left: '-9999px',
                width: 1,
                height: 1,
                opacity: 0,
                pointerEvents: 'none',
              }}
            />
            <input
              type="password"
              name="prevent_autofill_password"
              autoComplete="new-password"
              tabIndex={-1}
              aria-hidden
              style={{
                position: 'fixed',
                left: '-9999px',
                width: 1,
                height: 1,
                opacity: 0,
                pointerEvents: 'none',
              }}
            />
            <DialogContent
              dividers
              sx={{
                p: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                height: 'min(85vh, 720px)',
                minHeight: 'min(85vh, 720px)',
                maxHeight: 'min(85vh, 720px)',
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <BasicTabs
                tabs={dynamicTabs}
                value={tabsValue}
                handleChange={handleChange}
                onAddEvent={handleAddEvent}
              >
                <Box sx={{ minHeight: 420, height: 420, px: 2, pt: 1, pb: 2 }}>
                <TabPanel value={tabsValue} index={0}>
                  <LoadCustomerForm
                    companySuggestions={companySuggestions}
                    companySuggestionsLoading={isFetchingUserCompanies}
                    onCompanySelected={handleCompanySelected}
                  />
                </TabPanel>
                <TabPanel value={tabsValue} index={1}>
                  <LoadRateForm />
                </TabPanel>
                {hasPickup &&
                  pickupIndices.map((eventIndex, idx) => {
                    const tabIndex = 2 + idx
                    const currentOrderEvents = formMethods.watch('orderEvents') || []
                    const event = currentOrderEvents[eventIndex] as { _orderEventId?: string }
                    const canDelete = !event?._orderEventId
                    return (
                      <TabPanel key={`pickup-${eventIndex}`} value={tabsValue} index={tabIndex}>
                        <Box sx={{ position: 'relative' }}>
                          {canDelete && (
                            <IconButton
                              onClick={() => handleDeleteEvent(eventIndex)}
                              sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                zIndex: 10,
                                color: 'error.main',
                              }}
                              size="small"
                            >
                              <CloseIcon />
                            </IconButton>
                          )}
                          <LoadPickupForm
                            eventIndex={eventIndex}
                            enableCompanySuggestions
                          />
                        </Box>
                      </TabPanel>
                    )
                  })}
                {hasDelivery &&
                  deliveryIndices.map((eventIndex, idx) => {
                    const tabIndex = 2 + pickupCount + idx
                    const currentOrderEvents = formMethods.watch('orderEvents') || []
                    const event = currentOrderEvents[eventIndex] as { _orderEventId?: string }
                    const canDelete = !event?._orderEventId
                    return (
                      <TabPanel key={`delivery-${eventIndex}`} value={tabsValue} index={tabIndex}>
                        <Box sx={{ position: 'relative' }}>
                          {canDelete && (
                            <IconButton
                              onClick={() => handleDeleteEvent(eventIndex)}
                              sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                zIndex: 10,
                                color: 'error.main',
                              }}
                              size="small"
                            >
                              <CloseIcon />
                            </IconButton>
                          )}
                          <LoadDeliveryForm
                            eventIndex={eventIndex}
                            enableCompanySuggestions
                          />
                        </Box>
                      </TabPanel>
                    )
                  })}
                <TabPanel value={tabsValue} index={2 + pickupCount + deliveryCount}>
                  <LoadNotesForm />
                </TabPanel>
                </Box>
              </BasicTabs>
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Attach load confirmation (optional)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <input
                    id="manual-lc-upload"
                    ref={fileInputRef}
                    type="file"
                    hidden
                    disabled={isBusy}
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      setAttachFile(f ?? null)
                    }}
                  />
                  <Link
                    component="button"
                    type="button"
                    disabled={isBusy}
                    sx={{ fontStyle: 'italic' }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {attachFile ? attachFile.name : 'Click here to browse files'}
                  </Link>
                  {attachFile && (
                    <Button size="small" disabled={isBusy} onClick={() => setAttachFile(null)}>
                      Clear file
                    </Button>
                  )}
                </Box>
              </Box>

              {submitError && (
                <Box sx={{ px: 2, pb: 1 }}>
                  <Typography color="error" variant="body2">
                    {submitError}
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  py: 1.5,
                  px: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isBusy}
                  sx={{
                    minWidth: 170,
                    fontWeight: 700,
                    textTransform: 'capitalize',
                    '&.Mui-disabled': {
                      bgcolor: 'action.disabledBackground',
                      color: 'action.disabled',
                    },
                  }}
                  startIcon={
                    isBusy ? <CircularProgress size={18} color="inherit" /> : undefined
                  }
                >
                  Create order
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  disabled={isBusy}
                  onClick={onClose}
                  sx={{
                    minWidth: 170,
                    fontWeight: 700,
                    textTransform: 'capitalize',
                    bgcolor: 'grey.500',
                    '&:hover': { bgcolor: 'grey.600' },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
            </DialogContent>
          </form>
        </FormProvider>
      </EditModeProvider>
      <ConfirmationDialog
        title="Create order"
        open={confirmIncompleteOpen}
        handleClose={() => setConfirmIncompleteOpen(false)}
        successBtnText="Create anyway"
        handleSuccessBtn={() => {
          setConfirmIncompleteOpen(false)
          void performCreate()
        }}
      >
        Some recommended fields are missing or incomplete. Do you still want to create this order?
      </ConfirmationDialog>
    </Dialog>
  )
}

export default CreateManualOrderDialog
